/**
 * 🔥 WEBSITE ROAST COMPONENT
 * ═══════════════════════════════════════════════════════════════
 * 
 * The viral hook. Enter any URL, watch AI gods roast it.
 * Zero friction. Instant value. Shareable results.
 * 
 * ═══════════════════════════════════════════════════════════════
 */

import React, { useState } from 'react';
import { Loader2, Share2, Copy, Check, ExternalLink, Flame, Trophy, Swords } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// God colors for styling
const GOD_STYLES = {
  plutus: { bg: 'from-green-500/20 to-green-600/10', border: 'border-green-500/30', text: 'text-green-400' },
  athena: { bg: 'from-blue-500/20 to-blue-600/10', border: 'border-blue-500/30', text: 'text-blue-400' },
  eris: { bg: 'from-red-500/20 to-red-600/10', border: 'border-red-500/30', text: 'text-red-400' },
  hermes: { bg: 'from-purple-500/20 to-purple-600/10', border: 'border-purple-500/30', text: 'text-purple-400' },
  nike: { bg: 'from-amber-500/20 to-amber-600/10', border: 'border-amber-500/30', text: 'text-amber-400' },
  hestia: { bg: 'from-orange-500/20 to-orange-600/10', border: 'border-orange-500/30', text: 'text-orange-400' },
  apollo: { bg: 'from-yellow-500/20 to-yellow-600/10', border: 'border-yellow-500/30', text: 'text-yellow-400' }
};

// Score color based on value
const getScoreColor = (score) => {
  if (score >= 80) return 'text-green-400 border-green-500/50 bg-green-500/10';
  if (score >= 60) return 'text-yellow-400 border-yellow-500/50 bg-yellow-500/10';
  if (score >= 40) return 'text-orange-400 border-orange-500/50 bg-orange-500/10';
  return 'text-red-400 border-red-500/50 bg-red-500/10';
};

// Single god's roast card
const RoastCard = ({ roast, index }) => {
  const style = GOD_STYLES[roast.god] || GOD_STYLES.athena;
  
  return (
    <div 
      className={`p-5 rounded-xl bg-gradient-to-br ${style.bg} border ${style.border} animate-fadeIn`}
      style={{ animationDelay: `${index * 0.15}s` }}
    >
      <div className="flex items-center gap-3 mb-3">
        <span className="text-3xl">{roast.emoji}</span>
        <div>
          <h4 className={`font-bold ${style.text}`}>{roast.name}</h4>
          <p className="text-xs text-gray-500">{roast.title} • {roast.focus}</p>
        </div>
      </div>
      <p className="text-gray-300 leading-relaxed">{roast.content}</p>
    </div>
  );
};

// Main roast input component for landing page
export const RoastInput = ({ onRoastComplete }) => {
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleRoast = async () => {
    if (!url.trim()) {
      setError('Please enter a URL');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/roast`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: url.trim() })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to roast website');
      }

      if (onRoastComplete) {
        onRoastComplete(data);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full max-w-2xl mx-auto">
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="flex-1 relative">
          <input
            type="text"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && !loading && handleRoast()}
            placeholder="Enter any website URL..."
            className="w-full px-5 py-4 bg-gray-900/80 border border-gray-700 focus:border-orange-500/50 focus:ring-2 focus:ring-orange-500/20 rounded-xl text-white placeholder-gray-500 focus:outline-none transition-all text-lg"
            disabled={loading}
          />
          {loading && (
            <div className="absolute right-4 top-1/2 -translate-y-1/2">
              <Loader2 className="w-5 h-5 text-orange-400 animate-spin" />
            </div>
          )}
        </div>
        <button
          onClick={handleRoast}
          disabled={loading}
          className="px-8 py-4 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-400 hover:to-red-400 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold rounded-xl transition-all flex items-center justify-center gap-2 shadow-lg shadow-orange-500/25 hover:shadow-orange-500/40"
        >
          {loading ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Roasting...
            </>
          ) : (
            <>
              <Flame className="w-5 h-5" />
              Roast It
            </>
          )}
        </button>
      </div>
      {error && (
        <p className="mt-3 text-red-400 text-sm text-center">{error}</p>
      )}
      <p className="mt-3 text-gray-500 text-sm text-center">
        Free • No signup required • Results in ~15 seconds
      </p>
    </div>
  );
};

// Full roast results display
export const RoastResults = ({ roastData, onClose, onShare }) => {
  const [copied, setCopied] = useState(false);

  if (!roastData) return null;

  const shareUrl = `${window.location.origin}/roast/${roastData.roast_id}`;

  const handleCopy = async () => {
    await navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Website Roast: ${roastData.title || roastData.url}`,
          text: roastData.summary,
          url: shareUrl
        });
      } catch (e) {
        handleCopy();
      }
    } else {
      handleCopy();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/90 flex items-start justify-center z-50 overflow-y-auto py-8 px-4">
      <div className="bg-gray-900 border border-gray-800 rounded-2xl max-w-4xl w-full shadow-2xl">
        {/* Header */}
        <div className="p-6 border-b border-gray-800">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 text-orange-400 text-sm font-medium mb-2">
                <Flame className="w-4 h-4" />
                WEBSITE ROAST COMPLETE
              </div>
              <h2 className="text-2xl font-bold text-white mb-1">
                {roastData.title || 'Untitled Website'}
              </h2>
              <a 
                href={roastData.url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-gray-300 text-sm flex items-center gap-1"
              >
                {roastData.url}
                <ExternalLink className="w-3 h-3" />
              </a>
            </div>
            
            {/* Score */}
            <div className={`px-4 py-3 rounded-xl border ${getScoreColor(roastData.score)} text-center`}>
              <div className="flex items-center gap-1 mb-1">
                <Trophy className="w-4 h-4" />
                <span className="text-xs uppercase tracking-wide">Score</span>
              </div>
              <div className="text-3xl font-bold">{roastData.score}</div>
            </div>
          </div>
          
          {/* Summary */}
          <div className="mt-4 p-4 bg-gray-800/50 rounded-xl">
            <p className="text-gray-300 font-medium">{roastData.summary}</p>
          </div>
        </div>

        {/* Roasts */}
        <div className="p-6 space-y-4">
          <h3 className="text-lg font-bold text-white flex items-center gap-2">
            <span>⚔️</span> The Gods Have Spoken
          </h3>
          <div className="grid gap-4">
            {roastData.roasts.map((roast, index) => (
              <RoastCard key={roast.god} roast={roast} index={index} />
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-800 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button
              onClick={handleShare}
              className="px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-lg flex items-center gap-2 transition-all"
            >
              <Share2 className="w-4 h-4" />
              Share Roast
            </button>
            <button
              onClick={handleCopy}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg flex items-center gap-2 transition-all"
            >
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              {copied ? 'Copied!' : 'Copy Link'}
            </button>
          </div>
          
          <button
            onClick={onClose}
            className="px-6 py-2 border border-gray-700 hover:bg-gray-800 text-gray-300 rounded-lg transition-all"
          >
            Close
          </button>
        </div>

        {/* CTA */}
        <div className="p-6 bg-gradient-to-r from-purple-900/30 to-orange-900/30 border-t border-gray-800 rounded-b-2xl text-center">
          <p className="text-gray-300 mb-3">
            Want the gods to build your strategy? Get personalized debates and action plans.
          </p>
          <button
            onClick={() => window.location.href = '/signup'}
            className="px-8 py-3 bg-gradient-to-r from-purple-600 to-cyan-500 hover:from-purple-500 hover:to-cyan-400 text-white font-bold rounded-xl transition-all shadow-lg"
          >
            🏛️ Enter The Pantheon - Free
          </button>
        </div>
      </div>

      {/* Animation styles */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.4s ease-out forwards;
          opacity: 0;
        }
      `}</style>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// COMPETITIVE LEARNING COMPONENT
// ═══════════════════════════════════════════════════════════════

// Insight card for comparison
const InsightCard = ({ insight, index }) => {
  const style = GOD_STYLES[insight.god] || GOD_STYLES.athena;
  
  return (
    <div 
      className={`p-5 rounded-xl bg-gradient-to-br ${style.bg} border ${style.border} animate-fadeIn`}
      style={{ animationDelay: `${index * 0.1}s` }}
    >
      <div className="flex items-center gap-3 mb-4">
        <span className="text-3xl">{insight.emoji}</span>
        <div>
          <h4 className={`font-bold ${style.text}`}>{insight.name}</h4>
          <p className="text-xs text-gray-500">{insight.focus}</p>
        </div>
      </div>
      
      {insight.they_do_well && (
        <div className="mb-3">
          <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">They Do This Well</p>
          <p className="text-gray-300 text-sm">{insight.they_do_well}</p>
        </div>
      )}
      
      {insight.you_could_learn && (
        <div className="mb-3">
          <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">You Could Learn</p>
          <p className="text-gray-300 text-sm">{insight.you_could_learn}</p>
        </div>
      )}
      
      {insight.quick_win && (
        <div className="p-3 bg-black/30 rounded-lg border border-gray-700">
          <p className="text-xs text-green-400 uppercase tracking-wide mb-1 flex items-center gap-1">
            <span>⚡</span> Quick Win
          </p>
          <p className="text-white text-sm font-medium">{insight.quick_win}</p>
        </div>
      )}
    </div>
  );
};

// Compare input component
export const CompareInput = ({ onCompareComplete }) => {
  const [yourUrl, setYourUrl] = useState('');
  const [competitorUrl, setCompetitorUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleCompare = async () => {
    if (!yourUrl.trim() || !competitorUrl.trim()) {
      setError('Please enter both URLs');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/roast/compare`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          your_url: yourUrl.trim(),
          competitor_url: competitorUrl.trim()
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to compare websites');
      }

      if (onCompareComplete) {
        onCompareComplete(data);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full max-w-2xl mx-auto">
      <div className="space-y-3">
        <div className="relative">
          <label className="text-xs text-gray-500 uppercase tracking-wide mb-1 block">Your Website</label>
          <input
            type="text"
            value={yourUrl}
            onChange={(e) => setYourUrl(e.target.value)}
            placeholder="https://your-site.com"
            className="w-full px-5 py-3 bg-gray-900/80 border border-gray-700 focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 rounded-xl text-white placeholder-gray-500 focus:outline-none transition-all"
            disabled={loading}
          />
        </div>
        
        <div className="flex justify-center">
          <span className="text-gray-600 text-sm">vs</span>
        </div>
        
        <div className="relative">
          <label className="text-xs text-gray-500 uppercase tracking-wide mb-1 block">Competitor to Learn From</label>
          <input
            type="text"
            value={competitorUrl}
            onChange={(e) => setCompetitorUrl(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && !loading && handleCompare()}
            placeholder="https://competitor.com"
            className="w-full px-5 py-3 bg-gray-900/80 border border-gray-700 focus:border-cyan-500/50 focus:ring-2 focus:ring-cyan-500/20 rounded-xl text-white placeholder-gray-500 focus:outline-none transition-all"
            disabled={loading}
          />
        </div>
        
        <button
          onClick={handleCompare}
          disabled={loading}
          className="w-full px-8 py-4 bg-gradient-to-r from-purple-500 to-cyan-500 hover:from-purple-400 hover:to-cyan-400 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold rounded-xl transition-all flex items-center justify-center gap-2 shadow-lg shadow-purple-500/25 hover:shadow-purple-500/40"
        >
          {loading ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Analyzing (~30 seconds)...
            </>
          ) : (
            <>
              <span>🎓</span>
              Learn From Competitor
            </>
          )}
        </button>
      </div>
      {error && (
        <p className="mt-3 text-red-400 text-sm text-center">{error}</p>
      )}
      <p className="mt-3 text-gray-500 text-sm text-center">
        Free • No signup required • 7 AI gods analyze what you can learn
      </p>
    </div>
  );
};

// Comparison results display
export const CompareResults = ({ compareData, onClose }) => {
  const [copied, setCopied] = useState(false);

  if (!compareData) return null;

  const shareUrl = `${window.location.origin}/compare/${compareData.compare_id}`;

  const handleCopy = async () => {
    await navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const { summary } = compareData;
  const learningScore = summary?.learning_score || 5;

  return (
    <div className="fixed inset-0 bg-black/90 flex items-start justify-center z-50 overflow-y-auto py-8 px-4">
      <div className="bg-gray-900 border border-gray-800 rounded-2xl max-w-4xl w-full shadow-2xl">
        {/* Header */}
        <div className="p-6 border-b border-gray-800">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 text-purple-400 text-sm font-medium mb-2">
                <span>🎓</span>
                COMPETITIVE LEARNING COMPLETE
              </div>
              <div className="flex items-center gap-3 text-lg">
                <span className="text-white font-bold">{compareData.your_site?.title || 'Your Site'}</span>
                <span className="text-gray-500">learning from</span>
                <span className="text-cyan-400 font-bold">{compareData.competitor_site?.title || 'Competitor'}</span>
              </div>
            </div>
            
            {/* Learning Score */}
            <div className={`px-4 py-3 rounded-xl border text-center ${
              learningScore >= 8 ? 'border-green-500/50 bg-green-500/10 text-green-400' :
              learningScore >= 5 ? 'border-yellow-500/50 bg-yellow-500/10 text-yellow-400' :
              'border-orange-500/50 bg-orange-500/10 text-orange-400'
            }`}>
              <div className="flex items-center gap-1 mb-1">
                <span>📚</span>
                <span className="text-xs uppercase tracking-wide">Learning Value</span>
              </div>
              <div className="text-3xl font-bold">{learningScore}/10</div>
            </div>
          </div>
          
          {/* Big Picture Summary */}
          {summary?.big_picture && (
            <div className="mt-4 p-4 bg-gradient-to-r from-purple-900/30 to-cyan-900/30 rounded-xl border border-purple-500/20">
              <p className="text-gray-300">{summary.big_picture}</p>
            </div>
          )}
          
          {/* Top Priority */}
          {summary?.top_priority && (
            <div className="mt-3 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
              <p className="text-xs text-yellow-400 uppercase tracking-wide mb-1 flex items-center gap-1">
                <span>🎯</span> Top Priority
              </p>
              <p className="text-white font-medium">{summary.top_priority}</p>
            </div>
          )}
        </div>

        {/* Insights Grid */}
        <div className="p-6 space-y-4 max-h-[50vh] overflow-y-auto">
          <h3 className="text-lg font-bold text-white flex items-center gap-2">
            <span>⚔️</span> God Insights
          </h3>
          <div className="grid gap-4 md:grid-cols-2">
            {compareData.insights?.map((insight, index) => (
              <InsightCard key={insight.god} insight={insight} index={index} />
            ))}
          </div>
        </div>

        {/* Quick Wins Summary */}
        {summary?.quick_wins?.length > 0 && (
          <div className="px-6 pb-6">
            <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-xl">
              <h4 className="text-green-400 font-bold mb-3 flex items-center gap-2">
                <span>⚡</span> All Quick Wins
              </h4>
              <ul className="space-y-2">
                {summary.quick_wins.map((win, i) => (
                  <li key={i} className="text-gray-300 text-sm flex items-start gap-2">
                    <span className="text-green-400 mt-0.5">✓</span>
                    {win}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="p-6 border-t border-gray-800 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button
              onClick={handleCopy}
              className="px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-lg flex items-center gap-2 transition-all"
            >
              {copied ? <Check className="w-4 h-4" /> : <Share2 className="w-4 h-4" />}
              {copied ? 'Copied!' : 'Share Analysis'}
            </button>
          </div>
          
          <button
            onClick={onClose}
            className="px-6 py-2 border border-gray-700 hover:bg-gray-800 text-gray-300 rounded-lg transition-all"
          >
            Close
          </button>
        </div>

        {/* CTA */}
        <div className="p-6 bg-gradient-to-r from-purple-900/30 to-cyan-900/30 border-t border-gray-800 rounded-b-2xl text-center">
          <p className="text-gray-300 mb-3">
            Want the gods to help you implement these insights? Get personalized strategy debates.
          </p>
          <button
            onClick={() => window.location.href = '/signup'}
            className="px-8 py-3 bg-gradient-to-r from-purple-600 to-cyan-500 hover:from-purple-500 hover:to-cyan-400 text-white font-bold rounded-xl transition-all shadow-lg"
          >
            🏛️ Enter The Pantheon - Free
          </button>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.4s ease-out forwards;
          opacity: 0;
        }
      `}</style>
    </div>
  );
};

// Combined widget with tabs for Roast vs Compare
export const RoastCompareWidget = () => {
  const [mode, setMode] = useState('roast'); // 'roast', 'compare', or 'beat'
  const [roastData, setRoastData] = useState(null);
  const [compareData, setCompareData] = useState(null);
  const [battleData, setBattleData] = useState(null);

  return (
    <div>
      {/* Mode Toggle */}
      <div className="flex justify-center mb-6">
        <div className="inline-flex bg-gray-800 rounded-xl p-1">
          <button
            onClick={() => setMode('roast')}
            data-testid="mode-roast-btn"
            className={`px-5 py-2 rounded-lg font-medium transition-all text-sm ${
              mode === 'roast' 
                ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white' 
                : 'text-gray-400 hover:text-white'
            }`}
          >
            <Flame className="w-4 h-4 inline mr-1" /> Roast
          </button>
          <button
            onClick={() => setMode('compare')}
            data-testid="mode-compare-btn"
            className={`px-5 py-2 rounded-lg font-medium transition-all text-sm ${
              mode === 'compare' 
                ? 'bg-gradient-to-r from-purple-500 to-cyan-500 text-white' 
                : 'text-gray-400 hover:text-white'
            }`}
          >
            <Trophy className="w-4 h-4 inline mr-1" /> Learn
          </button>
          <button
            onClick={() => setMode('beat')}
            data-testid="mode-beat-btn"
            className={`px-5 py-2 rounded-lg font-medium transition-all text-sm ${
              mode === 'beat' 
                ? 'bg-gradient-to-r from-red-600 to-amber-500 text-white' 
                : 'text-gray-400 hover:text-white'
            }`}
          >
            <Swords className="w-4 h-4 inline mr-1" /> Beat
          </button>
        </div>
      </div>

      {/* Input Component */}
      {mode === 'roast' && <RoastInput onRoastComplete={setRoastData} />}
      {mode === 'compare' && <CompareInput onCompareComplete={setCompareData} />}
      {mode === 'beat' && <BeatInput onBeatComplete={setBattleData} />}

      {/* Results */}
      {roastData && (
        <RoastResults roastData={roastData} onClose={() => setRoastData(null)} />
      )}
      {compareData && (
        <CompareResults compareData={compareData} onClose={() => setCompareData(null)} />
      )}
      {battleData && (
        <BattleResults battleData={battleData} onClose={() => setBattleData(null)} />
      )}
    </div>
  );
};

// Compact roast widget for embedding
// ==================== BEAT MY COMPETITOR ====================

const BeatInput = ({ onBeatComplete }) => {
  const [yourUrl, setYourUrl] = useState('');
  const [competitorUrl, setCompetitorUrl] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleBeat = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_URL}/api/roast/beat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ your_url: yourUrl, competitor_url: competitorUrl, email })
      });
      if (!response.ok) throw new Error('Analysis failed');
      const data = await response.json();
      onBeatComplete(data);
    } catch (err) {
      setError('Analysis timed out or failed. This can happen with slow-loading sites. Please try again.');
    }
    setLoading(false);
  };

  return (
    <form onSubmit={handleBeat} className="space-y-3" data-testid="beat-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <input
          type="text"
          value={yourUrl}
          onChange={(e) => setYourUrl(e.target.value)}
          placeholder="Your website URL"
          required
          data-testid="beat-your-url"
          className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-white placeholder-gray-500 text-sm focus:border-red-500 focus:outline-none transition-all"
          disabled={loading}
        />
        <input
          type="text"
          value={competitorUrl}
          onChange={(e) => setCompetitorUrl(e.target.value)}
          placeholder="Competitor URL to beat"
          required
          data-testid="beat-competitor-url"
          className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-white placeholder-gray-500 text-sm focus:border-red-500 focus:outline-none transition-all"
          disabled={loading}
        />
      </div>
      <div className="flex gap-3">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Email (get your battle report)"
          data-testid="beat-email"
          className="flex-1 px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-white placeholder-gray-500 text-sm focus:border-red-500 focus:outline-none transition-all"
          disabled={loading}
        />
        <button
          type="submit"
          disabled={loading || !yourUrl || !competitorUrl}
          data-testid="beat-submit-btn"
          className="px-8 py-3 bg-gradient-to-r from-red-600 to-amber-500 text-white font-bold rounded-xl text-sm hover:shadow-lg hover:shadow-red-500/25 transition-all disabled:opacity-50 whitespace-nowrap"
        >
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Swords className="w-4 h-4 inline mr-1" /> Battle!</>}
        </button>
      </div>
      {error && <p className="text-red-400 text-sm text-center">{error}</p>}
      {loading && (
        <p className="text-amber-400 text-sm text-center animate-pulse">
          The gods are analyzing your competitor's weaknesses... (30-45 seconds)
        </p>
      )}
    </form>
  );
};

const BattleResults = ({ battleData, onClose }) => {
  const [copied, setCopied] = useState(false);
  const { insights, summary } = battleData;

  const handleCopy = () => {
    const url = `${window.location.origin}/battle/${battleData.battle_id}`;
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const scoreColor = summary?.battle_score >= 7 ? 'text-green-400' : summary?.battle_score >= 4 ? 'text-amber-400' : 'text-red-400';
  const scoreLabel = summary?.battle_score >= 7 ? 'Very Beatable' : summary?.battle_score >= 4 ? 'Competitive Fight' : 'Tough Opponent';

  return (
    <div className="mt-8 space-y-6" data-testid="battle-results">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-xl font-bold text-white">Battle Report</h3>
          <p className="text-sm text-gray-400">
            {battleData.your_site?.title || battleData.your_site?.url} vs {battleData.competitor_site?.title || battleData.competitor_site?.url}
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={handleCopy} data-testid="battle-share-btn" className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-gray-300 hover:bg-gray-700 transition-colors">
            {copied ? <Check className="w-4 h-4 inline mr-1" /> : <Copy className="w-4 h-4 inline mr-1" />}
            {copied ? 'Copied!' : 'Share'}
          </button>
          <button onClick={onClose} className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-gray-300 hover:bg-gray-700 transition-colors">
            Close
          </button>
        </div>
      </div>

      {/* Battle Score */}
      {summary && (
        <div className="bg-gradient-to-r from-red-900/30 to-amber-900/20 border border-red-500/20 rounded-xl p-6" data-testid="battle-summary">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm text-gray-400 mb-1">Beatability Score</p>
              <div className="flex items-baseline gap-2">
                <span className={`text-4xl font-black ${scoreColor}`}>{summary.battle_score}</span>
                <span className="text-gray-500">/10</span>
                <span className={`text-sm font-semibold ${scoreColor}`}>{scoreLabel}</span>
              </div>
            </div>
          </div>
          <p className="text-white font-medium mb-2">{summary.battle_assessment}</p>
          <div className="grid md:grid-cols-2 gap-4 mt-4">
            <div className="bg-black/30 rounded-lg p-4">
              <p className="text-xs text-red-400 font-semibold uppercase tracking-wider mb-1">Biggest Vulnerability</p>
              <p className="text-gray-300 text-sm">{summary.biggest_vulnerability}</p>
            </div>
            <div className="bg-black/30 rounded-lg p-4">
              <p className="text-xs text-amber-400 font-semibold uppercase tracking-wider mb-1">#1 Priority</p>
              <p className="text-gray-300 text-sm">{summary.top_priority}</p>
            </div>
          </div>
          {summary.kill_shots?.length > 0 && (
            <div className="mt-4 bg-black/30 rounded-lg p-4">
              <p className="text-xs text-red-400 font-semibold uppercase tracking-wider mb-2">Kill Shots (Do This Week)</p>
              <ul className="space-y-1">
                {summary.kill_shots.map((shot, i) => (
                  <li key={i} className="text-gray-300 text-sm flex items-start gap-2">
                    <span className="text-red-400 mt-0.5">&#9876;</span> {shot}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* God Insights */}
      <div className="space-y-4">
        {insights?.filter(i => !i.error).map((insight) => (
          <div key={insight.god} className="bg-gray-900/50 border border-gray-800 rounded-xl p-5" data-testid={`battle-insight-${insight.god}`}>
            <div className="flex items-center gap-3 mb-3">
              <span className="text-xl">{insight.emoji}</span>
              <div>
                <p className="font-semibold text-white">{insight.name}</p>
                <p className="text-xs text-gray-500">{insight.title} — {insight.focus}</p>
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              <div className="bg-red-900/20 border border-red-800/30 rounded-lg p-3">
                <p className="text-xs text-red-400 font-semibold uppercase mb-1">Their Weakness</p>
                <p className="text-gray-300 text-sm">{insight.weakness}</p>
              </div>
              <div className="bg-green-900/20 border border-green-800/30 rounded-lg p-3">
                <p className="text-xs text-green-400 font-semibold uppercase mb-1">Your Advantage</p>
                <p className="text-gray-300 text-sm">{insight.advantage}</p>
              </div>
            </div>
            <div className="mt-3 bg-amber-900/20 border border-amber-800/30 rounded-lg p-3">
              <p className="text-xs text-amber-400 font-semibold uppercase mb-1">Attack Plan</p>
              <p className="text-gray-300 text-sm">{insight.attack_plan}</p>
            </div>
            {insight.kill_shot && (
              <div className="mt-2 flex items-start gap-2 text-sm">
                <span className="text-red-400 font-bold">Kill Shot:</span>
                <span className="text-gray-300">{insight.kill_shot}</span>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* CTA */}
      <div className="text-center p-6 bg-gradient-to-r from-purple-900/30 to-cyan-900/20 border border-purple-500/20 rounded-xl">
        <p className="text-white font-bold mb-2">Ready to go deeper?</p>
        <p className="text-gray-400 text-sm mb-4">Get the full War Room to debate your complete go-to-market strategy.</p>
        <a
          href="/free-debate"
          data-testid="battle-cta-warroom"
          className="inline-block px-8 py-3 bg-gradient-to-r from-purple-600 to-cyan-600 text-white font-semibold rounded-full hover:shadow-lg hover:shadow-purple-500/25 transition-all"
        >
          Try the Living War Room Free
        </a>
      </div>
    </div>
  );
};


export const RoastWidget = () => {
  const [roastData, setRoastData] = useState(null);

  return (
    <>
      <RoastInput onRoastComplete={setRoastData} />
      {roastData && (
        <RoastResults 
          roastData={roastData} 
          onClose={() => setRoastData(null)}
        />
      )}
    </>
  );
};

export default RoastWidget;
